import React, { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { hasConsent } from '@/lib/consent';
import { useMiniCart } from '@/contexts/MiniCartContext';
import {
  NEWSLETTER_SOURCE_POPUP,
  NewsletterSignupStatus,
  hasNewsletterSignupCompleted,
  markNewsletterPopupDismissed,
  signupNewsletter,
  wasNewsletterPopupDismissedWithin,
} from '@/lib/newsletter';

/** Delay before the popup appears after page load. */
export const POPUP_DELAY_MS = 15000;
/** Scroll depth (0–1) that also triggers the popup. */
const SCROLL_DEPTH_TRIGGER = 0.5;
const DISMISS_DAYS = 14;

/** Routes where the popup must never appear (checkout / cart flow). */
const SUPPRESSED_PATH_PREFIXES = ['/bestellung', '/checkout', '/newsletter'];


export const NewsletterPopup: React.FC = () => {
  const { language, t } = useLanguage();
  const { pathname } = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [state, setState] = useState<NewsletterSignupStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const triggered = useRef(false);
  const { isOpen: miniCartOpen } = useMiniCart();
  const miniCartOpenRef = useRef(miniCartOpen);
  miniCartOpenRef.current = miniCartOpen;
  const [pending, setPending] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);

  const suppressed = SUPPRESSED_PATH_PREFIXES.some((p) => pathname.startsWith(p));

  useEffect(() => {
    if (triggered.current || suppressed) return;
    if (hasNewsletterSignupCompleted()) return;
    if (wasNewsletterPopupDismissedWithin(DISMISS_DAYS)) return;

    let timer = 0;
    const open = () => {
      if (triggered.current) return;
      triggered.current = true;
      // Never on top of the mini cart — wait until it closes.
      if (miniCartOpenRef.current) setPending(true);
      else setIsOpen(true);
    };
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max > 0 && window.scrollY / max >= SCROLL_DEPTH_TRIGGER) open();
    };
    // Desktop exit intent: pointer leaves through the top edge.
    const isDesktop = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const onMouseOut = (e: MouseEvent) => {
      if (!e.relatedTarget && e.clientY <= 0) open();
    };

    // Wait for the cookie banner to be handled so both modals never overlap.
    const poll = window.setInterval(() => {
      if (!hasConsent() || triggered.current) return;
      window.clearInterval(poll);
      timer = window.setTimeout(open, POPUP_DELAY_MS);
      window.addEventListener('scroll', onScroll, { passive: true });
      if (isDesktop) document.addEventListener('mouseout', onMouseOut);
      onScroll();
    }, 400);

    return () => {
      window.clearInterval(poll);
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('mouseout', onMouseOut);
      if (timer) window.clearTimeout(timer);
    };
  }, [suppressed]);

  useEffect(() => {
    if (pending && !miniCartOpen && !suppressed) {
      setPending(false);
      setIsOpen(true);
    }
  }, [pending, miniCartOpen, suppressed]);

  // Hide if the user navigates into checkout or opens the mini cart.
  useEffect(() => {
    if (isOpen && (suppressed || miniCartOpen)) {
      setIsOpen(false);
      if (!suppressed) setPending(true);
    }
  }, [isOpen, suppressed, miniCartOpen]);

  const close = useCallback(() => {
    setIsOpen(false);
    markNewsletterPopupDismissed();
  }, []);

  // Escape to close + scroll lock while open.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, close]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError(null);
    const result = await signupNewsletter(email.trim(), language, NEWSLETTER_SOURCE_POPUP);
    setIsSubmitting(false);
    if (!result) {
      setError(t.newsletter.error);
      return;
    }
    setState(result);
    setEmail('');
    // 14-day pause also after a signup.
    markNewsletterPopupDismissed();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-black/40 backdrop-blur-sm sm:items-center sm:px-4 sm:py-6"
      onClick={close}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="newsletter-popup-title"
        onClick={(e) => e.stopPropagation()}
        className="relative max-h-[85dvh] w-full overflow-y-auto rounded-t-3xl border pb-[env(safe-area-inset-bottom)] animate-in slide-in-from-bottom duration-300 sm:max-h-[calc(100dvh-3rem)] sm:max-w-[460px] sm:rounded-3xl sm:pb-0 sm:slide-in-from-bottom-0 sm:fade-in border-white/80 bg-white/90 shadow-2xl backdrop-blur-xl"
      >
        <button
          ref={closeRef}
          type="button"
          onClick={close}
          aria-label={t.newsletter.popupClose}
          className="absolute right-4 top-4 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full border border-h2h-border bg-white/80 text-h2h-charcoal transition-colors hover:bg-white"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>

        <div className="relative z-[1] px-5 py-8 text-center sm:px-9 sm:py-10">
          <h2 id="newsletter-popup-title" className="h2h-sh px-8 pt-2">
            {t.newsletter.popupTitle} <em>{t.newsletter.popupTitleEm}</em>
          </h2>
          <p className="h2h-sp mx-auto max-w-[380px]">
            {t.newsletter.popupSentence}
          </p>

          {state ? (
            <div
              role="status"
              aria-live="polite"
              className="mt-6 rounded-3xl border border-h2h-border bg-white/80 px-5 py-6"
            >
              {state === 'pending' ? (
                <>
                  <p className="text-base font-bold text-h2h-charcoal">
                    {t.newsletter.popupSuccess}
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-h2h-charcoal/80">
                    {t.newsletter.pendingBody}
                  </p>
                </>
              ) : (
                <p className="text-sm leading-relaxed text-h2h-charcoal">
                  {t.newsletter.alreadyConfirmed}
                </p>
              )}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-6">
              <label htmlFor="newsletter-popup-email" className="sr-only">
                {t.newsletter.placeholder}
              </label>
              <input
                id="newsletter-popup-email"
                name="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.newsletter.placeholder}
                className="h-12 w-full rounded-full border border-h2h-border bg-white/80 px-5 text-base text-h2h-charcoal outline-none transition-colors placeholder:text-h2h-light focus:border-h2h-charcoal focus:bg-white"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="mt-3 inline-flex h-12 w-full items-center justify-center rounded-full bg-h2h-charcoal px-7 text-sm font-bold text-white transition-colors hover:bg-[#333] disabled:cursor-not-allowed disabled:opacity-70"
              >
                {isSubmitting ? t.newsletter.submitting : t.newsletter.submit}
              </button>
              {error ? <p className="mt-3 text-sm text-[#9c3f33]">{error}</p> : null}
            </form>
          )}

          <p className="mt-4 text-xs text-h2h-charcoal/70">{t.newsletter.finePrint} {t.newsletter.note}</p>
          <p className="mt-2 text-xs text-h2h-charcoal/70">
            {t.newsletter.consentPrefix}
            <a href="/datenschutz" className="underline underline-offset-2">
              {t.newsletter.consentLink}
            </a>
            {t.newsletter.consentSuffix}
          </p>
        </div>
      </div>
    </div>
  );
};
